/** @jsxRuntime classic */
/** @jsx React.createElement */
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import type { MarketTickerQuote } from "./market-ticker-types";

type SparklineSpan = "1d" | "5d";

export function MarketTickerSparkline({
  quote,
  positive,
}: {
  quote: MarketTickerQuote;
  positive: boolean;
}) {
  const preferredSpan = useMemo(() => getPreferredSpan(quote), [quote]);
  const [span, setSpan] = useState<SparklineSpan>(preferredSpan);

  useEffect(() => {
    setSpan((current) =>
      hasSpanData(quote, current) ? current : preferredSpan,
    );
  }, [preferredSpan, quote]);

  const points = getPoints(quote, span);
  return (
    <div className="relative mt-3">
      <div className="fintheon-market-ticker-span-toggle absolute right-1 top-1 z-10 flex overflow-hidden rounded border text-[8px] uppercase tracking-[0.08em]">
        {(["1d", "5d"] as const).map((value) =>
          React.createElement(
            "button",
            {
              key: value,
              type: "button",
              disabled: !hasSpanData(quote, value),
              onClick: () => setSpan(value),
              className: `px-1.5 py-0.5 ${
                span === value
                  ? "bg-[var(--fintheon-accent)] text-[#050402]"
                  : hasSpanData(quote, value)
                    ? "text-[var(--fintheon-muted)]"
                    : "text-[#4b463a]"
              }`,
            },
            value.toUpperCase(),
          ),
        )}
      </div>
      <SparklineSvg
        points={points}
        quote={quote}
        positive={positive}
        span={span}
      />
    </div>
  );
}

function SparklineSvg({
  points,
  quote,
  positive,
  span,
}: {
  points: MarketTickerQuote["sparkline"];
  quote: MarketTickerQuote;
  positive: boolean;
  span: SparklineSpan;
}) {
  if (points.length < 2) {
    return (
      <div className="fintheon-market-ticker-chart-panel flex h-[74px] items-center justify-center rounded border text-[10px] text-[var(--fintheon-muted)]">
        {span.toUpperCase()} chart unavailable
      </div>
    );
  }
  const width = 260;
  const height = 74;
  const pad = 6;
  const values = getValues(points, quote, span);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spanRange = max - min || 1;
  const line = points
    .map((point, index) => {
      const projected = projectPoint({
        value: point.close,
        index,
        count: points.length,
        width,
        height,
        pad,
        min,
        span: spanRange,
      });
      return `${projected.x.toFixed(2)},${projected.y.toFixed(2)}`;
    })
    .join(" ");
  const high = projectMarker(
    points,
    "high",
    width,
    height,
    pad,
    min,
    spanRange,
  );
  const low = projectMarker(points, "low", width, height, pad, min, spanRange);
  return (
    <svg
      className="fintheon-market-ticker-chart-panel fintheon-sparkline h-[74px] w-full overflow-visible rounded border"
      viewBox={`0 0 ${width} ${height}`}
      data-tone={positive ? "bullish" : "bearish"}
    >
      {high ? <GuideLine y={high.y} width={width} pad={pad} /> : null}
      {low ? <GuideLine y={low.y} width={width} pad={pad} /> : null}
      <polyline
        points={line}
        fill="none"
        className="fintheon-sparkline__path"
        strokeLinecap="round"
        strokeWidth="2"
      />
      {high ? <Marker point={high} label="High" /> : null}
      {low ? <Marker point={low} label="Low" /> : null}
    </svg>
  );
}

function getPreferredSpan(quote: MarketTickerQuote): SparklineSpan {
  if (hasRealSpanData(quote, "5d")) return "5d";
  return "1d";
}

function getPoints(quote: MarketTickerQuote, span: SparklineSpan) {
  if (span === "1d" && quote.sparkline1d.length > 0) return quote.sparkline1d;
  if (span === "5d" && quote.sparkline5d.length > 0) return quote.sparkline5d;
  if (span === "1d") return buildSessionProxyPoints(quote);
  return quote.sparkline;
}

function hasSpanData(quote: MarketTickerQuote, span: SparklineSpan) {
  if (hasRealSpanData(quote, span)) return true;
  return span === "1d" && buildSessionProxyPoints(quote).length >= 2;
}

function hasRealSpanData(quote: MarketTickerQuote, span: SparklineSpan) {
  if (span === "1d") return quote.sparkline1d.length >= 2;
  return (quote.sparkline5d.length || quote.sparkline.length) >= 2;
}

function getValues(
  points: MarketTickerQuote["sparkline"],
  quote: MarketTickerQuote,
  span: SparklineSpan,
) {
  const values = points.flatMap((point) =>
    [point.close, point.high, point.low].filter(isFiniteNumber),
  );
  if (span === "5d" && isFiniteNumber(quote.rolling5dHigh))
    values.push(quote.rolling5dHigh);
  if (span === "5d" && isFiniteNumber(quote.rolling5dLow))
    values.push(quote.rolling5dLow);
  return values;
}

function buildSessionProxyPoints(
  quote: MarketTickerQuote,
): MarketTickerQuote["sparkline"] {
  const asOf = Date.parse(quote.asOf);
  const end = Number.isFinite(asOf) ? asOf : Date.now();
  const start = firstFiniteNumber(quote.previousClose, quote.open);
  const lows = [quote.low, quote.price, start].filter(isFiniteNumber);
  const highs = [quote.high, quote.price, start].filter(isFiniteNumber);
  if (!isFiniteNumber(start) || lows.length === 0 || highs.length === 0)
    return [];
  const low = Math.min(...lows);
  const high = Math.max(...highs);
  const final = isFiniteNumber(quote.price) ? quote.price : start;
  const step = 30 * 60 * 1000;
  const path =
    quote.change >= 0 ? [start, low, high, final] : [start, high, low, final];
  return path.map((close, index) => ({
    time: end - (path.length - index - 1) * step,
    close,
    high: Math.max(close, high),
    low: Math.min(close, low),
  }));
}

function firstFiniteNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (isFiniteNumber(value)) return value;
  }
  return null;
}

function projectMarker(
  points: MarketTickerQuote["sparkline"],
  mode: "high" | "low",
  width: number,
  height: number,
  pad: number,
  min: number,
  span: number,
) {
  const marker = points.reduce<null | { index: number; value: number }>(
    (best, point, index) => {
      const value = point[mode] ?? point.close;
      if (!isFiniteNumber(value)) return best;
      if (!best) return { index, value };
      if (mode === "high") return value > best.value ? { index, value } : best;
      return value < best.value ? { index, value } : best;
    },
    null,
  );
  return marker
    ? projectPoint({
        value: marker.value,
        index: marker.index,
        count: points.length,
        width,
        height,
        pad,
        min,
        span,
      })
    : null;
}

function projectPoint(input: {
  value: number;
  index: number;
  count: number;
  width: number;
  height: number;
  pad: number;
  min: number;
  span: number;
}) {
  return {
    x:
      input.pad +
      (input.index / (input.count - 1)) * (input.width - input.pad * 2),
    y:
      input.height -
      input.pad -
      ((input.value - input.min) / input.span) * (input.height - input.pad * 2),
  };
}

function GuideLine({
  y,
  width,
  pad,
}: {
  y: number;
  width: number;
  pad: number;
}) {
  return (
    <line
      x1={pad}
      x2={width - pad}
      y1={y}
      y2={y}
      className="fintheon-sparkline__guide"
      strokeDasharray="2 5"
    />
  );
}

function Marker({
  point,
  label,
}: {
  point: { x: number; y: number };
  label: string;
}) {
  return (
    <circle
      cx={point.x}
      cy={point.y}
      r="2.5"
      className="fintheon-sparkline__marker"
    >
      <title>{label}</title>
    </circle>
  );
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}
